import { H2 } from '@/components'
import { Button } from '@/components_deprecated'
import { getVehicleExpirationInput } from '@/modules/vehicles/expirations/expiration-input'
import { useVehicleExpirationHandlers } from '@/modules/vehicles/expirations/use-vehicle-expiration-handlers'
import { VehicleExpirationsTable } from '@/modules/vehicles/expirations/vehicle-expirations-table'
import { VEHICLE_EXPIRATIONS_QUERY } from '@/modules/vehicles/queries'
import type { EditVehicleExpirationPromptResolve } from '@/modules/vehicles/types'
import type {
  VehicleExpirationsQuery,
  VehicleExpirationsQueryVariables,
} from '@/modules/vehicles/types.graphql'
import { useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import { useVehicleExpirationsFilterForm } from '../hooks'
import { useVehicleExpirationMutations } from '../mutations.hooks'
import { EditVehicleExpirationModal } from './EditVehicleExpirationModal'
import { VehicleExpirationFilter } from './VehicleExpirationFilter'

interface VehicleExpirationsProps {
  vehicleId: string
}

export const VehicleExpirations: React.FC<VehicleExpirationsProps> = ({
  vehicleId,
}) => {
  const translations = useTranslate({
    expirationDates: 'vehicles.expiration-dates',
    add: 'common.add',
  })

  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const { hasPermissions } = usePermissions()
  const canAdd = hasPermissions(
    PERMISSIONS.vehicles.add.vehicleexpiration,
    true
  )
  const { formValues: filter, updateForm } = useVehicleExpirationsFilterForm()

  const { data, loading } = useQuery<
    VehicleExpirationsQuery,
    VehicleExpirationsQueryVariables
  >(VEHICLE_EXPIRATIONS_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      vehicle: vehicleId,
      expirationTypes: filter.expirationTypes,
      orderBy: filter.orderBy ?? 'expirationDate',
    },
    onError: onErrorAuto(),
  })

  const expirations = useMemo(() => {
    return data?.allVehicleExpirations?.edges?.map(edge => edge?.node) || []
  }, [data])

  const { createExpiration } = useVehicleExpirationMutations()
  const { editExpiration, removeExpiration, moveToServices } =
    useVehicleExpirationHandlers()

  async function handleCreate() {
    const { data } = await addPrompt<EditVehicleExpirationPromptResolve | null>(
      resolve => <EditVehicleExpirationModal onSubmit={resolve} />
    )
    if (!data) return

    await createExpiration({
      variables: {
        input: {
          ...getVehicleExpirationInput(data.form, data.hasNotification),
          vehicle: vehicleId,
        },
      },
    })
  }

  return (
    <div className="mt-4">
      <div className="my-4 flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center md:gap-0">
        <H2>{translations.expirationDates}</H2>
        <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center">
          <VehicleExpirationFilter filter={filter} onUpdate={updateForm} />
          {canAdd && (
            <Button
              iconLeftProps={{ icon: 'plus', size: '1rem', type: 'solid' }}
              height="3rem"
              onClick={handleCreate}
            >
              {translations.add}
            </Button>
          )}
        </div>
      </div>

      <VehicleExpirationsTable
        expirations={expirations}
        loading={loading}
        showVehicle={false}
        onEdit={editExpiration}
        onDelete={removeExpiration}
        onMoveToServices={moveToServices}
      />
    </div>
  )
}
